import { classNames } from 'primereact/utils';
/**
 * Taken from react-pdf-highlighter-extended to be able
 * to tweak styling.
 */
import type { CSSProperties, MouseEvent } from 'react';
import type { ViewportHighlight } from 'react-pdf-highlighter-extended';
import classes from './TextHighlight.module.css';

/**
 * The props type for {@link TextHighlight}.
 *
 * @category Component Properties
 */
export interface TextHighlightProps {
  /**
   * Highlight to render over text.
   */
  highlight: ViewportHighlight;

  /**
   * Callback triggered whenever the user clicks on the part of a highlight.
   *
   * @param event - Mouse event associated with click.
   */
  onClick?(event: MouseEvent<HTMLDivElement>): void;

  /**
   * Callback triggered whenever the user enters the area of a text highlight.
   *
   * @param event - Mouse event associated with movement.
   */
  onMouseOver?(event: MouseEvent<HTMLDivElement>): void;

  /**
   * Callback triggered whenever the user leaves  the area of a text highlight.
   *
   * @param event - Mouse event associated with movement.
   */
  onMouseOut?(event: MouseEvent<HTMLDivElement>): void;

  /**
   * Indicates whether the component is autoscrolled into view, affecting
   * default theming.
   */
  isScrolledTo: boolean;

  /**
   * Callback triggered whenever the user tries to open context menu on highlight.
   *
   * @param event - Mouse event associated with click.
   */
  onContextMenu?(event: MouseEvent<HTMLDivElement>): void;

  /**
   * Optional CSS styling applied to each TextHighlight part.
   */
  style?: CSSProperties;
}

/**
 * A component for displaying a highlighted text area.
 *
 * @category Component
 */
export const TextHighlight = ({
  highlight,
  onClick,
  onMouseOver,
  onMouseOut,
  isScrolledTo,
  onContextMenu,
  style,
}: TextHighlightProps) => {
  const { rects } = highlight.position;

  return (
    <div
      className={classNames({
        [classes.TextHighlight]: true,
        [classes.scrolledTo]: isScrolledTo,
      })}
      onContextMenu={onContextMenu}
    >
      <div className={classes.parts}>
        {rects.map((rect, index) => (
          // biome-ignore lint/a11y: Copied as-is, also it's pdf view
          <div
            // biome-ignore lint/a11y: Copied as-is, also it's pdf view
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            onClick={onClick}
            // biome-ignore lint/suspicious/noArrayIndexKey: Copied as-is from react-highlighter
            key={index}
            style={{ ...rect, ...style }}
            className={classes.part}
          />
        ))}
      </div>
    </div>
  );
};
